/**
 * AppToaster — the app shell's single toast surface.
 *
 * Wraps sonner's `<Toaster>` so the toast palette comes from the proof. design
 * tokens instead of sonner's own. `richColors` (mint-fill success / rose-fill
 * error) was removed: those are third-party semantic fills, and design.md §2
 * reserves red/green to `--add` / `--del` alone. sonner's DEFAULT palette is no
 * better — `--normal-bg: #fff` and `--normal-text: hsl(0 0% 9%)` are exactly the
 * pure white + cool grey that design.md §4.2's absolute rules forbid — so the
 * variables are re-pointed here rather than simply dropping the prop.
 *
 * The colour variables ride on the toaster element's inline `style` (sonner
 * spreads it onto `<ol data-sonner-toaster>`), which beats sonner's injected
 * stylesheet no matter what order the two land in. Everything that cannot be a
 * variable — shadow removal, the structural error/success rule — lives in
 * AppToaster.css.
 *
 * Refs: design.md §2 (one reserved accent, hairlines over shadows), §4.1
 * (Inter shell face), §4.2 (warm neutrals; --add/--del only for diffs).
 */

import { Toaster } from "sonner";
import type { CSSProperties } from "react";
import "./AppToaster.css";

/**
 * Token bridge. Keys are sonner's own custom properties; values are proof.
 * tokens, so a theme swap propagates to toasts with no extra wiring.
 */
const TOASTER_STYLE = {
  // Base toast surface.
  "--normal-bg": "var(--surface)",
  "--normal-text": "var(--text)",
  "--normal-border": "var(--line-strong)",
  "--normal-bg-hover": "var(--bg)",
  "--normal-border-hover": "var(--line-strong)",
  // sonner's internal grey ramp — used by the close button and action chips.
  "--gray1": "var(--surface)",
  "--gray2": "var(--bg)",
  "--gray4": "var(--line-strong)",
  "--gray5": "var(--line-strong)",
  "--gray11": "var(--muted)",
  "--gray12": "var(--text)",
  "--border-radius": "var(--radius-button, 8px)",
  fontFamily: "var(--font-sans)",
} as CSSProperties;

export function AppToaster() {
  return <Toaster position="top-center" closeButton style={TOASTER_STYLE} />;
}
